import {I18n} from 'aws-amplify/utils';
import {
    Drawer,
    DrawerProps,
    ScrollArea,
} from '@mantine/core';

import {RenameForm} from '../forms/RenameForm';
import {RenameFormValues} from '../forms/types';
import {useRenameProviderConnectionMutation} from '../hooks/useProviderConnectionsQuery';
import {ProviderConnection} from '../types';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1304 — THE RENAME DRAWER.
//
// ⚠️ A DRAWER, NOT AN EDIT PAGE. The module has no edit route and two drawers
// already (connect, re-validate); a third matching one is the shape this module
// has, and an EditPage would introduce a route and a page for a single text
// field. It also keeps the tenant on the detail page, where the name they are
// changing is visible behind the drawer.
//
// ⚠️ IT CLOSES ON SUCCESS, like the re-validate drawer and unlike connect. The
// connect drawer stays open because it has something to hand over — a
// freshly-minted external id the tenant has not seen. A rename mints nothing and
// changes nothing the tenant needs to copy, so it closes and the detail page
// behind it shows the new name.
//
// ⚠️ THE SUCCESS MESSAGE DOES NOT MENTION STATUS. A rename cannot change it, so
// saying anything about the connection's health here would invite the reading
// that renaming re-checked it. The re-validate drawer's message reports an
// outcome because a rotation HAS one; this one reports an action.

export const RenameDrawer = (
    {
        connection,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        connection: ProviderConnection,
    },
) => {

    const renameMutation = useRenameProviderConnectionMutation();

    const handleSubmit = async (values: RenameFormValues) => {

        try {
            // Trimmed at the boundary, matching the API, which trims in the
            // resolver AND the Lambda. A name of trailing spaces is a name the
            // tenant cannot see the end of.
            await renameMutation.mutateAsync({
                id: connection.id,
                displayName: values.displayName.trim(),
            });

            showSuccessNotification(I18n.get('Connection renamed.'));
            onClose();

        } catch (e) {
            // The call failed. The drawer stays open with what the tenant typed.
            showErrorNotifications(e);
            throw e;
        }
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Rename connection')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <RenameForm
                connection={connection}
                handleSubmit={handleSubmit}
                handleCancel={onClose}
            />
        </Drawer>
    );
};
